/**
 * Number of pieces awaiting upload — `WS2_EA_CTR_PMQ.NB` (fiche 22080).
 *
 * Shared by the header bell (badge) and the Accueil « pièces en attente »
 * banner (Phase 3): both read the same cached query, so the counter is
 * fetched once per session/environment.
 */

import { useQuery } from '@tanstack/react-query';

import { fetchErpRecord } from '../api/erpProxy';
import { useExtranetAuthStore } from '../auth/store';
import { toFiniteNumber } from '../utils/format';

/** Endpoint slug of `WS2_EA_CTR_PMQ` (contract §6). */
export const CTR_PMQ_ENDPOINT = 'boby-ws2-ea-ctr-pmq';

/** One `WS2_EA_CTR_PMQ` row. */
export interface PendingPiecesCounterRow {
  NB: number | string | null;
}

/** Query key — scoped by environment and session (a switch reloads anyway). */
export function pendingPiecesCountQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
): readonly unknown[] {
  return ['extranet', 'pending-pieces-count', registrySlug, sessionId];
}

/** Read the counter off a `WS2_EA_CTR_PMQ` row (`null` row ⇒ 0). */
export function pendingPiecesCountOf(row: PendingPiecesCounterRow | null): number {
  const count = toFiniteNumber(row?.NB);
  return count === null || count < 0 ? 0 : count;
}

export interface UsePendingPiecesCountResult {
  /** Pending pieces count; `0` while loading or on error. */
  count: number;
  isLoading: boolean;
  error: Error | null;
}

export function usePendingPiecesCount(): UsePendingPiecesCountResult {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);

  const query = useQuery<number>({
    queryKey: pendingPiecesCountQueryKey(registrySlug, sessionId),
    queryFn: async () =>
      pendingPiecesCountOf(
        await fetchErpRecord<PendingPiecesCounterRow>(registrySlug, CTR_PMQ_ENDPOINT),
      ),
    enabled: registrySlug !== null,
    staleTime: 60_000,
    retry: false,
  });

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error,
  };
}
